import React from 'react'
import { Box, Card, CardContent, Skeleton, Divider } from '@mui/material'

export interface WalletPageShimmerProps {
    showBackButton?: boolean
}

const WalletPageShimmer: React.FC<WalletPageShimmerProps> = ({ showBackButton = false }) => {
    return (
        <Box
            sx={{
                bgcolor: 'background.default',
                pb: 10
            }}
        >
            {/* Page Title Shimmer */}
            <Box
                sx={{
                    px: 2,
                    py: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative'
                }}
            >
                {showBackButton && (
                    <Skeleton
                        variant="circular"
                        width={32}
                        height={32}
                        sx={{
                            position: 'absolute',
                            left: 8
                        }}
                    />
                )}
                <Skeleton variant="text" width={120} height={28} />
            </Box>

            {/* Balance Card Shimmer - White Background */}
            <Box sx={{ px: 0 }}>
                <Card
                    elevation={0}
                    sx={{
                        background: 'white',
                        borderRadius: 0
                    }}
                >
                    <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <Skeleton
                                variant="circular"
                                width={28}
                                height={28}
                                sx={{ bgcolor: 'grey.300' }}
                            />
                            <Skeleton
                                variant="text"
                                width={120}
                                height={20}
                                sx={{ bgcolor: 'grey.300' }}
                            />
                            <Skeleton
                                variant="circular"
                                width={24}
                                height={24}
                                sx={{ bgcolor: 'grey.300', ml: 'auto' }}
                            />
                        </Box>
                        <Skeleton variant="text" width={180} height={48} sx={{ bgcolor: 'grey.200', mb: 1 }} />
                        <Skeleton variant="text" width={200} height={16} sx={{ bgcolor: 'grey.200' }} />
                    </CardContent>
                </Card>
            </Box>

            {/* About Wallet Block Shimmer */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    px: 2,
                    py: 1.5,
                    bgcolor: 'background.paper',
                    borderTop: 1,
                    borderBottom: 1,
                    borderColor: 'divider'
                }}
            >
                <Skeleton
                    variant="circular"
                    width={32}
                    height={32}
                    sx={{ bgcolor: 'grey.300', mr: 1.5 }}
                />
                <Skeleton variant="text" width={150} height={20} sx={{ flex: 1 }} />
                <Skeleton
                    variant="circular"
                    width={24}
                    height={24}
                    sx={{ bgcolor: 'grey.300' }}
                />
            </Box>

            {/* Wallet Activity Heading Shimmer */}
            <Box sx={{ px: 2, pt: 3, pb: 1.5 }}>
                <Skeleton variant="text" width={120} height={24} />
            </Box>

            {/* Tabs Shimmer */}
            <Box
                sx={{
                    display: 'flex',
                    gap: 1,
                    px: 2,
                    py: 1.5,
                    borderBottom: 1,
                    borderColor: 'divider',
                    bgcolor: 'background.paper'
                }}
            >
                {[1, 2, 3].map((_, index) => (
                    <Skeleton key={index} variant="rectangular" width={80} height={32} sx={{ borderRadius: 2 }} />
                ))}
            </Box>

            {/* Transactions Section Shimmer */}
            <Box sx={{ px: 2, pt: 2 }}>
                {/* Transaction Cards Shimmer */}
                {Array.from({ length: 5 }).map((_, i) => (
                    <Card
                        key={i}
                        elevation={0}
                        sx={{
                            mb: 1.5,
                            bgcolor: 'background.paper',
                            borderRadius: 2,
                            border: 1,
                            borderColor: 'divider'
                        }}
                    >
                        <CardContent
                            sx={{
                                p: 2.5,
                                '&:last-child': {
                                    pb: 1.75
                                }
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 2,
                                    alignItems: 'flex-start'
                                }}
                            >
                                {/* Icon Shimmer */}
                                <Skeleton
                                    variant="circular"
                                    width={48}
                                    height={48}
                                    sx={{ bgcolor: 'grey.200', flexShrink: 0 }}
                                />

                                {/* Content - Title and Date */}
                                <Box sx={{ flex: 1, minWidth: 0, pt: 0.5 }}>
                                    <Skeleton variant="text" width="70%" height={20} sx={{ mb: 0.75 }} />
                                    <Skeleton variant="text" width="50%" height={16} />
                                </Box>

                                {/* Amount and Expiry - Right Side */}
                                <Box sx={{ textAlign: 'right', flexShrink: 0, pt: 0.5 }}>
                                    <Skeleton variant="text" width={80} height={20} sx={{ mb: 1 }} />
                                    <Skeleton
                                        variant="rectangular"
                                        width={100}
                                        height={22}
                                        sx={{ borderRadius: '12px', ml: 'auto' }}
                                    />
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                ))}
            </Box>
        </Box>
    )
}

export { WalletPageShimmer }
export default WalletPageShimmer

